import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Button, Modal, Col, Grid } from 'antd';

import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import { buyerCSAT } from 'v2/helpers/hotjar';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import { RefundBuyerRequestSchema } from './validations';

const { useBreakpoint } = Grid;

export interface IBuyerRefundReason {
  motive: string;
  detail: string;
  invoiceId: string;
}

interface IProps {
  open: boolean;
  invoiceId: string | number;
  onClose: () => void;
  onFinish: (refundReason: IBuyerRefundReason) => void;
}

export default function BuyerRefundRequestModal({ open, invoiceId, onClose, onFinish }: IProps) {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const [refundReason, setRefundReason] = useState<IBuyerRefundReason>();

  const [onSubmit] = useCallbackObservable(
    (values: IBuyerRefundReason, { setSubmitting, resetForm }: FormikProps<IBuyerRefundReason>) => {
      setSubmitting(true);

      return refundRequestFactory.sendBuyerRefundRequest(values).pipe(
        tap(response => {
          const refundReason: IBuyerRefundReason = {
            ...values,
            ...{
              success: response.success,
              motive: response.request_motive_id,
              detail: response.description,
              invoiceId: response.invoice_id
            }
          };
          setRefundReason(refundReason);
          setSubmitting(false);
          resetForm();
          toast.success('Solicitação realizada com sucesso!');
          onComplete();
          buyerCSAT();
        }),
        catchError(err => {
          setSubmitting(false);
          toast.error(err);
          return of(true);
        })
      );
    },
    []
  );

  const onComplete = useCallback(() => {
    onFinish(refundReason);
    setRefundReason(null);
  }, [onFinish, refundReason]);

  const onHandleClose = useCallback(
    (resetForm: () => void) => {
      setRefundReason(null);
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const motivesOptions = useMemo(
    () =>
      (motives || []).map(item => ({
        label: item.description,
        value: item.id
      })),
    [motives]
  );

  return (
    <Formik
      validationSchema={RefundBuyerRequestSchema}
      initialValues={{ motive: '', detail: '', invoiceId }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, submitForm, isValid, dirty, resetForm }) => {
        const shouldDisableConfirmBtn = !isValid || !dirty || isSubmitting;

        return (
          <Form>
            <Modal
              open={open}
              onCancel={() => onHandleClose(resetForm)}
              width={isMobile ? '100vw' : undefined}
              title='Motivo do reembolso'
              closable
              footer={
                <div className='flex justify-end gap-2'>
                  <Button onClick={() => onHandleClose(resetForm)}>Cancelar</Button>
                  <Button
                    loading={isSubmitting}
                    onClick={submitForm}
                    htmlType='submit'
                    type='primary'
                    disabled={shouldDisableConfirmBtn}
                    className='mr-4'
                  >
                    {isSubmitting ? 'Solicitando...' : 'Solicitar'}
                  </Button>
                </div>
              }
            >
              {loading || isSubmitting ? (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin />
                </div>
              ) : (
                <div className='w-full'>
                  <Col span={24}>
                    <Field id='motive' name='motive' options={motivesOptions} component={ListRadioButtonFormField} />
                  </Col>
                </div>
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
}
